import { I18n } from 'react-redux-i18n';
import { Flex } from '@common/components/Flex';
import { SORT_DIRECTION } from '@common/constants';
import cx from 'classnames';

import { getFilters } from '@common/actions/Filters';
import { MessageBox } from '@common/components/MessageBox';
import { ErrorIcon } from '@common/components/error-icon';
import { InfoIcon } from '@common/components/info-icon';
import { getTestAttributes } from '@common/utils/tests';
import { Tabs } from '@common/components/tabs';

import { Folders } from './components/folders';
import { IndividualsTable } from './components/individuals-table';
import { InvitesTable } from './components/invites-table';
import { Header } from './components/header';
import { getFolders } from './actions/folders';
import {
  applyCurrentColumnsToAllUsers,
  applyCurrentColumnsToSpecificProfiles,
  getAllowedFeatures,
  getIndividuals,
  getIndividualsColumns,
  getProfiles,
  restoreColumnsToDefault,
  saveIndividualsColumns,
  setDefaultColumnsForSpecificUsers,
  setDefaultColumnsForToAllUsers,
  renameIndividual,
  openDatasheet,
  openPedigree,
  generateLetter,
  showProbandsOnly,
  sendInvite
} from './actions/individuals';
import { getInvites, getInvitesColumns, saveInvitesColumns } from './actions/invites';
import { NewInvite } from './components/new-invite';
import { NewIndividual } from "./components/new-individual";
import { QuestionnaireOpen } from './components/questionnaire-open';
import { CONTEXT_TYPE } from './constants/context';
import { NewTableSortingDirection } from '../../common/components/new-table/new-table.types';

import './individuals.styles.scss';

export const MODULE_NAME = 'Individuals';



export class Individuals extends React.Component {
  individualDetailsTabsTypes = {
    invites: 'INVITES',
  };
  parentIndividualIdOfCurrentInvites = null;

  state = {
    selectedFolderId: null,
    probandsOnly: false,
    loadingIndividuals: true,
    search: '',
    folders: [],
    messageProps: {
      header: '',
      text: '',
      show: false,
      confirmText: 'Ok',
      cancelText: '',
      size: 'medium',
      onCustomOk: () => {},
    },

    individualsFilters: {},
    individuals: [],
    individualsTotalCount: 0,
    selectedIndividualsId: [],
    selectedIndividualDetailsTab: this.individualDetailsTabsTypes.invites,
    individualDetailsOpened: true,
    individualsTableColumns: [],
    individualsTableLoadMetadata: {
      pagination: {
        start: 0,
        size: 45,
      },
      sorting: {
        direction: NewTableSortingDirection.asc
      },
    },
    
    selectedInvitesId: [],
    invites: [],
    invitesTotalCount: 0,
    invitesTableColumns: [],
    invitesTableLoadMetadata: {
      pagination: {
        start: 0,
        size: 45,
      },
      sorting: {
        direction: SORT_DIRECTION.desc,
        columnIndex: 1,
      },
    },

    newInviteDialogProps: {
      individualId: null,
      opened: false,
    },

    openQuestionnaireDialogProps: {
      id: null,
      opened: false,
      itemName: '',
    },

    sendReminderDialogProps: {
      inviteId: null,
      individualId: null,
      opened: false,
    },
    newIndividualDialogProps: {
      opened: false,
    },
  };

  componentDidMount() {
    this.initialize();
  }

  initialize = async () => {
    try {
      this.getFilters();
      this.getInvitesTableColumns();
      const folders = await getFolders();
  
      this.setState((x) => ({ ...x, folders }));

      const defaultFolderId = this.getDefaultFolderId(folders);
  
      if (defaultFolderId) {
        this.setSelectedFolderId(defaultFolderId, false);
      }
  
      const probandsOnly = await this.showProbandsOnly();
      const individualsColumns = await this.getIndividualsTableColumns();
      await this.getIndividuals({
        columns: individualsColumns,
        selectedFolderId: defaultFolderId,
        probandsOnly,
        individuals: [],
        filtering: this.state.individualsTableLoadMetadata.filtering,
        pagination: this.state.individualsTableLoadMetadata.pagination,
        sorting: this.state.individualsTableLoadMetadata.sorting,
      });
    } catch (err) {
      const errorMessage = err?.entity || I18n.t('Failed to get data');

      this.showErrorMessage(errorMessage);
      console.error(err);
    }
  }

  getFilters = () => {
    getFilters().then((individualsFilters) => {
      this.setState((x) => ({
        ...x,
        individualsFilters,
      }));
    });
  }

  showProbandsOnly = async () => {
    try {
      const probandsOnly = await showProbandsOnly();
      this.setState((x) => ({
        ...x,
        probandsOnly: probandsOnly,
      }));

      return probandsOnly;
    } catch (err) {
      const errorMessage = err?.entity || I18n.t('Failed to get the value of the "Probands" dropdown');

      this.showErrorMessage(errorMessage);
      console.error(err);

      return false;
    }
  }

  getIndividualsTableColumns = async () => {
    try {
      const columns = await getIndividualsColumns();

      this.setState((x) => ({ ...x, individualsTableColumns: columns }));

      return columns;
    } catch (err) {
      const errorMessage = err?.entity || I18n.t('Failed to get individuals columns');

      this.showErrorMessage(errorMessage);
      console.error(err);
    }
  }

  getColumnId = (column) => {
    return column.field.id.toString();
  }

  getIndividuals = async (params) => {
    const { filtering, pagination, sorting, probandsOnly, search } = params;
    const columns = params.columns.map((x, i) => {
      const notSortableActionColumn = 1;
      const isSorted = i === sorting.index - notSortableActionColumn;

      return {
        ...x,
        sortOrder: isSorted ? 1 : 0,
        sortAsc: isSorted ? sorting.direction === NewTableSortingDirection.asc : false,
      };
    });

    const filters = filtering?.columns || {};
    const columnFiltersProcessed = Object.keys(filters).map((columnId) => {
      const columnIndex = params.columns.findIndex((x) => this.getColumnId(x) == columnId);
      const columnToRemoveBecauseOfActionsColumn = 1;
      const value = filters[columnId];

      return {
        columnId: columnIndex + columnToRemoveBecauseOfActionsColumn,
        operator1: value.operator1 ?? null,
        operator2: value.operator2 ?? null,
        value1: value.whereClause1 ?? null,
        value2: value.whereClause2 ?? null,
        andOr: value.andOR,
      };
    });

    try {
      const { data, totalCount } = await getIndividuals({
        pagination,
        filtering: {
          search: search ?? null,
          probandsOnly,
          filterApplied: filtering?.filter,
          columnFilters: columnFiltersProcessed,
          columns,
          folderId: params.selectedFolderId,
        },
      });

      const finalData = pagination.start > 0
        ? [...params.individuals, ...data]
        : data;

      this.setState((x) => ({
        ...x, 
        individualsTableLoadMetadata: {
          filtering,
          pagination,
          sorting,
        },
        individualsTableColumns: columns,
        individuals: finalData,
        individualsTotalCount: pagination.start > 0 ? x.individualsTotalCount : totalCount,
      }));
    } catch (err) {
      const errorMessage = err?.entity || I18n.t('Failed to get individuals');

      this.showErrorMessage(errorMessage);
      console.error(err);
    } finally {
      this.setState((x) => ({ ...x, loadingIndividuals: false }));
    }
  }

  getInvites = async (params = {
    columns: this.state.invitesTableColumns,
    invites: this.state.invites,
    selectedIndividualsId: this.state.selectedIndividualsId,
    filtering: this.state.invitesTableLoadMetadata.pagination, 
    pagination: this.state.invitesTableLoadMetadata.pagination, 
    sorting: this.state.invitesTableLoadMetadata.sorting,
  }) => {
    try {
      const { filtering, pagination, sorting } = params;
  
      const columns = params.columns.map((x, i) => {
        const notSortableActionColumn = 1;
        const isSorted = i === sorting.columnIndex - notSortableActionColumn;
  
        return {
          ...x,
          sortOrder: isSorted ? 1 : 0,
          sortAsc: isSorted ? sorting.direction === SORT_DIRECTION.asc : undefined,
        };
      });
      const individualId = params.selectedIndividualsId.length ? params.selectedIndividualsId[0] : null;
      const { data, totalCount } = await getInvites({
        pagination,
        filtering: {
          ...filtering,
          columns,
          folderId: individualId
        },
      });

      this.parentIndividualIdOfCurrentInvites = individualId;

      const finalData = pagination.start > 0
        ? [...params.invites, ...data]
        : data;
  
      this.setState((x) => ({
        ...x,
        invitesTableColumns: columns,
        invites: finalData,
        invitesTotalCount: x.invitesTotalCount || totalCount,
      }));
    } catch (err) {
      const errorMessage = err?.entity || I18n.t('Failed to get invites');

      this.showErrorMessage(errorMessage);
      console.error(err);
    }
  }

  getInvitesTableColumns = async () => {
    try {
      const columns = await getInvitesColumns();
  
      this.setState((x) => ({ ...x, invitesTableColumns: columns }));

      return columns;
    } catch (err) {
      const errorMessage = err?.entity || I18n.t('Failed to get invites columns');

      this.showErrorMessage(errorMessage);
      console.error(err);
    }
  }

  getDefaultFolderId(folders = this.state.folders) {
    const firstFolder = folders[0];

    return firstFolder?.id;
  }

  setSelectedFolderId = (id, refreshList = true, callback) => {
    if (this.state.selectedFolderId == id) {
      return;
    }

    this.setState(
      (state) => ({
        ...state,
        individualsTableLoadMetadata: {
          ...state.individualsTableLoadMetadata,
          pagination: {
            start: 0,
            size: 45,
          },
        },
        selectedFolderId: id,
        selectedIndividualsId: [],
        selectedInvitesId: [],
        individualsTotalCount: 0,
        invites: [],
        invitesTotalCount: 0,
      }), 
      refreshList 
        ? () => this.getIndividuals({
            search: this.state.search,
            columns: this.state.individualsTableColumns,
            selectedFolderId: id,
            probandsOnly: this.state.probandsOnly,
            individuals: [],
            filtering: this.state.individualsTableLoadMetadata.filtering,
            pagination: {
              start: 0,
              size: 45,
            },
            sorting: this.state.individualsTableLoadMetadata.sorting,
          })
        : callback
    );
  }

  handleSelectedIndividualChange = async (ids) => {
    const sameSelected = 
      this.state.selectedIndividualsId.length === 1 &&
      ids.length === 1 &&
      this.state.selectedIndividualsId[0] === ids[0];

    if (sameSelected) {
      return;
    }

    const finalInvites = ids.length > 1 ? this.state.invites : [];
    const finalInvitesLoadMetadata = ids.length > 1
      ? this.state.invitesTableLoadMetadata
      : {
        ...this.state.invitesTableLoadMetadata,
        pagination: {
          start: 0,
          size: 45,
        },
      };

    this.setState(
      (state) => ({
        ...state,
        invitesTableLoadMetadata: finalInvitesLoadMetadata,
        invites: finalInvites,
        selectedIndividualsId: ids,
        selectedInvitesId: [],
      }), 
    );

    if (ids.length === 1) {
      const invitesColumns = await this.getInvitesTableColumns();
      await this.getInvites({
        columns: invitesColumns,
        invites: finalInvites,
        selectedIndividualsId: ids,
        ...finalInvitesLoadMetadata,
      });
    }
  }

  handleSelectedInvitesChange = (ids) => {
    const sameSelected = 
      this.state.selectedInvitesId.length === 1 &&
      ids.length === 1 &&
      this.state.selectedInvitesId[0] === ids[0];

    if (sameSelected) {
      return;
    }

    this.setState(
      (state) => ({
        ...state,
        selectedInvitesId: ids,
      }), 
    );
  }

  handlePedigreeOpen = () => {
    const selectedIndividualId = this.state.selectedIndividualsId[0];
    openPedigree(selectedIndividualId);
  }

  handleDatasheetOpen = () => {
    const selectedIndividualId = this.state.selectedIndividualsId[0];
    openDatasheet(selectedIndividualId);
  }

  handleGenerateLetter = () => {
    const selectedIndividualId = this.state.selectedIndividualsId[0];
    generateLetter(selectedIndividualId);
  }

  get individualDetailsTabs() {
    return {
      [this.individualDetailsTabsTypes.invites]: {
        tabProps: {
          value: 'invites',
          label: I18n.t('Invites'),
          ...getTestAttributes(MODULE_NAME, 'individual-details', 'tab', 'invites'),
        },
        component: (
          <InvitesTable
            individualSelected={Boolean(this.state.selectedIndividualsId.length)}
            className="Individuals__content__folder-details__individual-details__information"
            getData={(loadMetadata) => this.getInvites({
              columns: this.state.invitesTableColumns,
              invites: this.state.invites,
              selectedIndividualsId: this.state.selectedIndividualsId,
              ...loadMetadata,
            })}
            individualId={this.state.selectedIndividualsId.length ? this.state.selectedIndividualsId[0] : undefined}
            showErrorMessage={this.showErrorMessage}
            data={this.state.invites}
            columns={this.state.invitesTableColumns}
            totalCount={this.state.invitesTotalCount}
            selectedInvitesId={this.state.selectedInvitesId}
            onSelectedInvitesChange={this.handleSelectedInvitesChange}
            loadMetadata={this.state.invitesTableLoadMetadata}
            showInfoMessage={this.showInfoMessage}
            getAllowedFeatures={getAllowedFeatures}
            setLoadMetadata={this.setInvitesLoadMetadata}
            onNewInviteOpen={this.handleNewInviteOpen}
            onBatchNewInviteOpen={this.handleNewInviteOpen}
            onQuestionnaireOpen={this.handleQuestionnaireOpen}
            onResize={this.handleInvitesColumnsResize}
            onColumnAdd={this.handleInvitesColumnsChange}
            onColumnRemove={this.handleInvitesColumnsChange}
            onReminderOpen={this.handleReminderOpen}
            onDatasheetOpen={this.handleDatasheetOpen}
            onPedigreeOpen={this.handlePedigreeOpen}
            onGenerateLetter={this.handleGenerateLetter}
          />
        ),
      }
    };
  }

  handleIndividualDetailsToggle = () => {
    this.setState((x) => ({
      ...x,
      individualDetailsOpened: !x.individualDetailsOpened,
    }));
  }

  setInvitesLoadMetadata = (metadata) => {
    this.setState((state) => ({ 
      ...state, 
      invitesTableLoadMetadata: { 
        ...state.invitesTableLoadMetadata, 
        ...metadata 
      } 
    }));
  }

  handleIndividualsColumnsResize = async (columns) => {
    try {
      const processedColumns = this.state.individualsTableColumns.map((x) => ({
        ...x,
        width: columns[x.field.id] || x.width,
      }));
  
      this.setState((x) => ({ ...x, individualsTableColumns: processedColumns }));
      await saveIndividualsColumns(processedColumns);
    } catch (err) {
      const errorMessage = err?.entity || I18n.t('Failed to save resized columns');

      this.showErrorMessage(errorMessage);
      console.error(err);
    }
  }

  handleInvitesColumnsResize = async (columns) => {
    try {
      const processedColumns = this.state.invitesTableColumns.map((x) => ({
        ...x,
        width: columns[x.field.id] || x.width,
      }));
  
      this.setState((x) => ({ ...x, invitesTableColumns: processedColumns }));
      await saveInvitesColumns(processedColumns);
    } catch (err) {
      const errorMessage = err?.entity || I18n.t('Failed to save resized columns');

      this.showErrorMessage(errorMessage);
      console.error(err);
    }
  }

  setMessageProps = (props) => {
    this.setState((state) => ({
      ...state,
      messageProps: {
        ...state.messageProps,
        confirmText: 'Ok',
        cancelText: '',
        size: 'small',
        ...props,
      },
    }));
  }

  showMessage = (config) => {
    this.setMessageProps({
      ...config,
      show: true,
      onCustomOk: this.closeMessage,
    });
  }

  closeMessage = () => {
    this.setMessageProps({ show: false });
  }

  showErrorMessage = (message) => {
    this.showMessage({
      header: I18n.t('Operation canceled'),
      content: <Flex style={{ overflow: 'auto' }} alignItems="center"><ErrorIcon className="Individuals__message-icon" /> {message}</Flex>,
      size: 'small'
      // TODO refactor with ErrorMessage component
    });
  }

  handleSetDefaultForAllUsers = async () => {
    try {
      await setDefaultColumnsForToAllUsers(this.state.individualsTableColumns);
    } catch (err) {
      this.showErrorMessage(err?.entity);
      console.error(err);
    }
  }

  handleSetDefaultForSpecificUSers = async (profiles) => {
    return setDefaultColumnsForSpecificUsers(this.state.individualsTableColumns, profiles);
  }

  handleApplyCurrentColumnsToAllUsers = async () => {
    try {
      await applyCurrentColumnsToAllUsers(this.state.individualsTableColumns);
    } catch (err) {
      this.showErrorMessage(err?.entity);
      console.error(err);
    }
  }

  handleApplyCurrentColumnsToSpecificUsers = async (profiles) => {
    return applyCurrentColumnsToSpecificProfiles(this.state.individualsTableColumns, profiles);
  }

  handleRestoreColumnToDefault = async () => {
    try {
      const columns = await restoreColumnsToDefault(this.state.individualsTableColumns);

      this.setState((x) => ({ ...x, individualsTableColumns: columns }));
    } catch (err) {
      this.showErrorMessage(err?.entity);
      console.error(err);
    }
  }

  handleIndividualsColumnsChange = async (columns) => {
    const newIndividuals = [];
    const newIndividualsLoadMetadata = {
      ...this.state.individualsTableLoadMetadata,
      filtering: {},
      pagination: {
        start: 0,
        size: 45,
      },
    };

    try {
      await saveIndividualsColumns(columns.map((x) => ({ ...x, field: { ...x.field, context: CONTEXT_TYPE.individual } })));  

      this.setState(
        (state) => ({
          ...state,
          individualsTableLoadMetadata: newIndividualsLoadMetadata,
          selectedIndividualsId: [],
          selectedInvitesId: [],
          individualsTotalCount: 0,
          invites: [],
          invitesTotalCount: 0,
        }), 
      );

      const individualsColumns = await this.getIndividualsTableColumns();
      await this.getIndividuals({
        columns: individualsColumns,
        selectedFolderId: this.state.selectedFolderId,
        individuals: newIndividuals,
        probandsOnly: this.state.probandsOnly,
        search: this.state.search,
        ...newIndividualsLoadMetadata,
      });
    } catch (err) {
      console.error(err);

      let errorMessage = I18n.t('Failed to save columns');
      const possibleErrors = err.entity?.errors;

      if (possibleErrors?.length) {
        errorMessage = possibleErrors[0];
      }

      if (typeof err.entity === 'string') {
        errorMessage = err.entity;
      }

      throw {
        entity: errorMessage,
      }
    }
  }

  handleInvitesColumnsChange = async (columns) => {
    const newInvitesLoadMetadata = {
      ...this.state.invitesTableLoadMetadata,
      filtering: {},
      pagination: {
        start: 0,
        size: 45,
      },
    };
    const newInvites = [];
    const newSelectedIndividualsId = [];

    try {
      await saveInvitesColumns(columns);

      this.setState(
        (state) => ({
          ...state,
          invitesTableLoadMetadata: newInvitesLoadMetadata,
          selectedIndividualsId: newSelectedIndividualsId,
          selectedInvitesId: [],
          invites: newInvites,
          invitesTotalCount: 0,
        }), 
      );
    } catch (err) {
      console.error(err);

      let errorMessage = I18n.t('Failed to save columns');
      const possibleErrors = err.entity?.errors;

      if (possibleErrors?.length) {
        errorMessage = possibleErrors[0];
      }

      if (typeof err.entity === 'string') {
        errorMessage = err.entity;
      }

      throw {
        entity: errorMessage,
      }
    }

    const invitesColumns = await this.getInvitesTableColumns();
    await this.getInvites({
      columns: invitesColumns,
      invites: newInvites,
      selectedIndividualsId: newSelectedIndividualsId, 
      ...newInvitesLoadMetadata,
    });
  }

  handleRenameIndividual = async (id, name) => {
    await renameIndividual(id, name);

    const newLoadMetadata = {
      ...state.individualsTableLoadMetadata,
      pagination: {
        start: 0,
        size: 45,
      },
    };
    const newIndividuals = [];
    
    this.setState(
      (state) => ({
        ...state,
        individualsTableLoadMetadata: newLoadMetadata,
        selectedIndividualsId: [],
        selectedInvitesId: [],
        individualsTotalCount: 0,
        invites: [],
        invitesTotalCount: 0,
      }), 
    );

    this.getIndividuals({
      columns: this.state.individualsTableColumns,
      selectedFolderId: this.state.selectedFolderId,
      individuals: newIndividuals,
      search: this.state.search,
      probandsOnly: this.state.probandsOnly,
      ...newLoadMetadata,
    });
  }
  
  handleNewInviteOpen = (individualId) => {
    this.setState((x) => ({
      ...x,
      newInviteDialogProps: {
        ...x.newInviteDialogProps,
        individualId: individualId ?? this.parentIndividualIdOfCurrentInvites,
        opened: true,
      },
    }));
  }

  closeNewInviteDialog = () => {
    this.setState((x) => ({
      ...x,
      newInviteDialogProps: {
        ...x.newInviteDialogProps,
        individualId: null,
        opened: false,
      },
    }));
  }

  handleQuestionnaireOpen = (data) => {
    this.setState((x) => ({
      ...x,
      openQuestionnaireDialogProps: {
        ...x.openQuestionnaireDialogProps,
        id: data.globalId ?? this.parentIndividualIdOfCurrentInvites,
        itemName: data.name,
        opened: true,
      },
    }));
  }

  closeQuestionnaireDialog = () => {
    this.setState((x) => ({
      ...x,
      openQuestionnaireDialogProps: {
        ...x.openQuestionnaireDialogProps,
        id: null,
        itemName: '',
        opened: false,
      },
    }));
  }

  handleReminderOpen = (data) => {
    this.setState((x) => ({
      ...x,
      sendReminderDialogProps: {
        ...x.sendReminderDialogProps,
        inviteId: data.id,
        individualId: this.parentIndividualIdOfCurrentInvites,
        opened: true,
      },
    }));
  }

  closeReminderDialog = () => {
    this.setState((x) => ({
      ...x,
      sendReminderDialogProps: {
        ...x.sendReminderDialogProps,
        inviteId: null,
        individualId: null,
        opened: false,
      },
    }));
  }

  showInfoMessage = (message) => {
    this.showMessage({
      header: I18n.t('Information Message'),
      content: <Flex><InfoIcon className="Individuals__message-icon" />  {message}</Flex>,
      confirmText: 'Ok',
      onCustomOk: this.closeMessage,
    });
  }

  handleNewInviteSubmit = () => {
    this.closeNewInviteDialog();
    this.showInfoMessage(I18n.t('The Questionnaire invitation emails were successfully submitted'));
  }

  handleNewReminderSubmit = () => {
    this.closeReminderDialog();
    this.showInfoMessage(I18n.t('The Questionnaire reminder email was successfully sent.'));
  }

  handleQuestionnaireSubmit = (url) => {
    window.location.href = url;
  }

  toggleNewIndividualDialog = (isOpened) => {
    this.setState((x) => ({
      ...x,
      newIndividualDialogProps: {
        ...x.newIndividualDialogProps,
        opened: isOpened,
      },
    }));
  }

  closeNewIndividualDialog = () => {
    this.toggleNewIndividualDialog(false);
  }

  openNewIndividualDialog = () => {
    this.toggleNewIndividualDialog(true);
  }

  handleNewIndividual = (globalId) => {
    this.closeNewIndividualDialog();
    openDatasheet(globalId);
  }

  handleHeaderFilter = (filteredFields) => {
    const state = {
      ...this.state,
      ...filteredFields,
      loadingIndividuals: true,
      individualsTableLoadMetadata: {
        ...this.state.individualsTableLoadMetadata,
        pagination: {
          start: 0,
          size: 45,
        },
      },
      selectedIndividualsId: [],
      selectedInvitesId: [],
      individualsTotalCount: 0,
      invites: [],
      invitesTotalCount: 0,
      individuals: []
    }
    this.setState(state);

    this.getIndividuals({
      search: state.search,
      probandsOnly: state.probandsOnly,
      columns: this.state.individualsTableColumns,
      selectedFolderId: this.state.selectedFolderId,
      individuals: [],
      ...state.individualsTableLoadMetadata
    });
  }

  render() {
    const {
      folders,
      selectedFolderId,
      individuals,
      individualsTotalCount,
      selectedIndividualsId,
      selectedIndividualDetailsTab,
      individualDetailsOpened,
      individualsTableColumns,
      individualsTableLoadMetadata,
      messageProps,
      individualsFilters,
      newInviteDialogProps,
      openQuestionnaireDialogProps,
      loadingIndividuals,
      sendReminderDialogProps,
      newIndividualDialogProps,
      probandsOnly
    } = this.state;

    return (
      <div className="Individuals">
        <MessageBox
          hideMessageBox={this.closeMessage}
          {...messageProps}
        />
        {newInviteDialogProps.opened && (
          <NewInvite 
            sendInvite={sendInvite}
            individualId={newInviteDialogProps.individualId}
            showErrorMessage={this.showErrorMessage}
            showInfoMessage={this.showInfoMessage}
            onClose={this.closeNewInviteDialog}
            onSubmit={this.handleNewInviteSubmit}
          />
        )}
        {openQuestionnaireDialogProps.opened && (
          <QuestionnaireOpen 
            itemId={openQuestionnaireDialogProps.id}
            itemName={openQuestionnaireDialogProps.itemName}
            showErrorMessage={this.showErrorMessage}
            onClose={this.closeQuestionnaireDialog}
            onSubmit={this.handleQuestionnaireSubmit}
          />
        )}
        {sendReminderDialogProps.opened && (
          <NewInvite
            sendInvite={sendInvite}
            individualId={sendReminderDialogProps.individualId}
            inviteId={sendReminderDialogProps.inviteId}
            showErrorMessage={this.showErrorMessage}
            onSubmit={this.handleNewReminderSubmit}
            onClose={this.closeReminderDialog}
          />
        )}
        <Header
          probandCategory={probandsOnly}
          className="Individuals__header"
          onFilterChange={this.handleHeaderFilter}
          onNewIndividualOpen={this.openNewIndividualDialog}
        />
        {newIndividualDialogProps.opened && (
          <NewIndividual
            folders={folders}
            currentFolderId={selectedFolderId}
            showErrorMessage={this.showErrorMessage}
            onSubmit={this.handleNewIndividual}
            onClose={this.closeNewIndividualDialog}
          />
        )}
        <Flex width="100%" wrap="nowrap" className="Individuals__content">
          <Folders 
            className="Individuals__content__folders"
            list={folders}
            selectedFolderId={selectedFolderId}
            onChange={this.setSelectedFolderId}
            totalIndividualsCount={individualsTotalCount}
          />
          <div className="Individuals__content__folder-details">
          <IndividualsTable
              className={cx(
                  'Individuals__content__folder-details__individuals-table',
                  {
                    'Individuals__content__folder-details__individuals-table--opened': !individualDetailsOpened
                  }
              )}
              loading={loadingIndividuals}
              filters={individualsFilters}
              columns={individualsTableColumns}
              data={individuals}
              totalCount={individualsTotalCount}
              getAllowedFeatures={getAllowedFeatures}
              getData={(loadMetadata) => this.getIndividuals({
                columns: this.state.individualsTableColumns,
                selectedFolderId: this.state.selectedFolderId,
                individuals: this.state.individuals,
                search: this.state.search,
                probandsOnly: this.state.probandsOnly,
                ...loadMetadata
              })}
              getColumnId={this.getColumnId}
              loadMetadata={individualsTableLoadMetadata}
              selectedIndividualsId={selectedIndividualsId}
              onSelectedIndividualsChange={this.handleSelectedIndividualChange}
              onResize={this.handleIndividualsColumnsResize}
              getProfiles={getProfiles}
              showErrorMessage={this.showErrorMessage}
              onSetDefaultForAllUSers={this.handleSetDefaultForAllUsers}
              onApplyCurrentColumnsToAllUsers={this.handleApplyCurrentColumnsToAllUsers}
              onRestoreColumnToDefault={this.handleRestoreColumnToDefault}
              onSetDefaultForSpecificUSers={this.handleSetDefaultForSpecificUSers}
              onApplyCurrentColumnsToSpecificUsers={this.handleApplyCurrentColumnsToSpecificUsers}
              onColumnsChange={this.handleIndividualsColumnsChange}
              showMessage={this.showMessage}
              onNewInviteOpen={this.handleNewInviteOpen}
              onQuestionnaireOpen={this.handleQuestionnaireOpen}
              onRenameIndividual={this.handleRenameIndividual}
          />
            <div 
              className={cx(
                'Individuals__content__folder-details__individual-details',
                {
                  'Individuals__content__folder-details__individual-details--opened': individualDetailsOpened 
                }
              )}
            >
              <Flex alignItems="flex-end" className="Individuals__content__folder-details__individual-details__header">
                <Tabs 
                  value="invites"
                  items={Object.keys(this.individualDetailsTabs).map((x) => this.individualDetailsTabs[x].tabProps)}
                />

                <button 
                  onClick={this.handleIndividualDetailsToggle}
                  className="Individuals__content__folder-details__individual-details__toggle-btn"
                  {...getTestAttributes(MODULE_NAME, 'individual-details-toggle', 'button')}
                >
                  <i className={cx('fa', individualDetailsOpened ? 'fa-angle-down' : 'fa-angle-up' )} />
                </button>
              </Flex>
              {individualDetailsOpened && this.individualDetailsTabs[selectedIndividualDetailsTab].component}
            </div>
          </div>
        </Flex>
      </div>
    );
  }
}
